import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import {
  Grid,
  Card,
  CardContent,
  Box,
  Tabs,
  Tab,
  Pagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from '@mui/material';
import { saveAs } from 'file-saver';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteVendorRequest,
  resetVendorState,
  fetchAllVendorsRequest,
  fetchVendorsRequest,
} from '../../redux/Vendors/actions/vendorActions';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../styles/UserStyles.css';
import ReusableTable from '../../component/ui/ReusableTable';
import SearchAndButtonTab from '../../component/ui/SearchAndButtonTab';
import nodata from '../../assets/nodata.jpg';
import {
  ADD_VENDOR,
  VIEW_VENDOR,
} from '../../component/common/Routes/AppRoutes';
import { customHeaders } from './CustomHeaders';
import Loading from '../../component/ui/Loading';
import CustomButton from '../../component/ui/FormFields/Button';
import { ConstructionOutlined } from '@mui/icons-material';
import axiosInstance from '../../redux/instance/AxiosInstance';

const VendorManagementList = () => {
  const user = useSelector((state) => state.auth.user);
  const permissions = useMemo(() => {
    let safePermissions = {};

    try {
      if (user?.permissions) {
        safePermissions =
          typeof user.permissions === 'string'
            ? JSON.parse(user.permissions)
            : user.permissions;
      }
    } catch (error) {
      safePermissions = {};
    }

    return safePermissions;
  }, [user]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const tab = location.state?.tab ?? 0;
  const pageNumber= location.state?.page ?? 1;
  const searchNavigateQuery=location.state?.searchQuery??'';

 

  const [searchQuery, setSearchQuery] = useState(searchNavigateQuery??"");
  const [searchColumn, setSearchColumn] = useState('');
  const [tabIndex, setTabIndex] = useState(tab);
  const [page, setPage] = useState(pageNumber);
  const [open, setOpen] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState(null);
  const rowsPerPage = 10;
  const searchTimerRef = useRef(null);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');

  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [debouncedSearchColumn, setDebouncedSearchColumn] = useState('');

  const {
    vendors: response,
    vendorDetails,
    error,
  } = useSelector((state) => state.vendors);

  const vendors = response?.data || [];
  const deleteLoading = vendorDetails?.deleteLoading;
  const viewListLoading = response?.loading;

  const deleteMessage = useSelector(
    (state) => state.vendors?.vendorDetails?.success
  );

  const fetchVendors = useCallback(
    (
      pageNumber = page,
      currentTabIndex = tabIndex,
      search = debouncedSearchQuery,
      searchBy = debouncedSearchColumn
    ) => {
      const approoveStatus =
        currentTabIndex === 0 ? 2 : currentTabIndex === 1 ? 1 : 3;
      const params = {
        approove_status: approoveStatus,
      };
      if (search && !searchBy) {
        params.pagination_required = false;
      }
      if (search) {
        params.pagination_required = true;
        params.pagesize = rowsPerPage;
        params.pageNo = pageNumber;
        params.search = search;

        if (searchBy && searchBy.trim() !== '') {
          params.search_by = searchBy;
        }
      } else {
        params.pagination_required = true;
        params.pagesize = rowsPerPage;
        params.pageNo = pageNumber;
      }

      dispatch(fetchVendorsRequest(params));
    },
    [dispatch, rowsPerPage]
  );

  const isInitialLoad = useRef(true);
  useEffect(() => {
    if (isInitialLoad.current) {
      dispatch(resetVendorState());
      isInitialLoad.current = false;
    }
  }, [dispatch]);

  useEffect(() => {
    if (searchTimerRef.current&!searchNavigateQuery) {
      clearTimeout(searchTimerRef.current);
    }
         
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setDebouncedSearchColumn(searchColumn);
    }, 3000);
    
    if(searchNavigateQuery){
       setDebouncedSearchQuery(searchQuery);
      setDebouncedSearchColumn(searchColumn);
    }

   
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, [searchQuery,searchNavigateQuery, searchColumn]);

  useEffect(() => {
    if (!isInitialLoad.current) {
      console.log("entered1")
      fetchVendors(page, tabIndex, debouncedSearchQuery, debouncedSearchColumn);
    }
  }, [
    page,
    tabIndex,

    debouncedSearchQuery,
    debouncedSearchColumn,
  ]);

  useEffect(() => {
    if (deleteMessage) {
      toast.success('Vendor Deleted Successfully', {
        position: 'top-center',
        autoClose: 3000,
        closeOnClick: true,
        pauseOnHover: false,
        onClose: () => {
          dispatch(resetVendorState());
          fetchVendors(1);        
        },
      });
    }
  }, [deleteMessage, fetchVendors, dispatch]);

  const handleSearch = useCallback((query, column) => {
    setSearchQuery(query);

    if (column !== undefined) {
      setSearchColumn(column);
    }
    setPage(1);
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, []);

  const handleColumnChange = useCallback((column) => {
    setSearchColumn(column);
  }, []);

  const handleExport = useCallback(() => {
    const approoveStatus = tabIndex === 0 ? 2 : tabIndex === 1 ? 1 : 3;
    const params = {
      approove_status: approoveStatus,
      pagination_required: false,
    };
    if (debouncedSearchQuery) {
      params.pagination_required = false;
      params.search = debouncedSearchQuery;

      if (debouncedSearchColumn && debouncedSearchColumn.trim() !== '') {
        params.search_by = debouncedSearchColumn;
      }
    } else {
      params.pagination_required = false;
    }
    const apiCall = axiosInstance.get(`/vendors/list`, { params });

    apiCall
      .then((response) => {
        const allVendorsData = response?.data?.data || [];

        if (!vendors.length) return;

        const csvHeaders = [
          'First Name,Last Name,Agency Name,Email,Phone,Company Reg Number,Address1,Address2,City,State,Country,Pincode,IATA Reg Number,Vendor Commission,Pan Number,Gstin Number,Company Reg Number,Status,Approove Status',
        ];

        const csvRows = allVendorsData.map((vendor) => {
          const escapeCsvField = (field) => {
            if (field === null || field === undefined) return '""';
            const stringField = String(field || '-');
            if (
              stringField.includes(',') ||
              stringField.includes('"') ||
              stringField.includes('\n')
            ) {
              return `"${stringField.replace(/"/g, '""')}"`;
            }
            return stringField;
          };

          return [
            escapeCsvField(vendor.first_name),
            escapeCsvField(vendor.last_name),
            escapeCsvField(vendor.agency_name),
            escapeCsvField(vendor.email),
            escapeCsvField(vendor.phone),
            escapeCsvField(vendor.company_reg_number),
            escapeCsvField(vendor.address1),
            escapeCsvField(vendor.address2),
            escapeCsvField(vendor.city_name),
            escapeCsvField(vendor.state_name),
            escapeCsvField(vendor.country_name),
            escapeCsvField(vendor.pincode),
            escapeCsvField(vendor.iata_reg_no),
            escapeCsvField(vendor.discount),

            escapeCsvField(vendor.pan_number || '-'),
            escapeCsvField(vendor.gstin_number || '-'),
            escapeCsvField(vendor.company_reg_number || '-'),
            escapeCsvField(vendor.status === 1 ? 'Active' : 'Inactive'),
            escapeCsvField(
              vendor.approove_status === 1
                ? 'Approved'
                : vendor.approove_status === 2
                ? 'Pending'
                : 'Rejected'
            ),
          ].join(',');
        });

        const csvContent = [csvHeaders, ...csvRows].join('\n');
        const blob = new Blob([csvContent], {
          type: 'text/csv;charset=utf-8;',
        });
        saveAs(blob, 'vendors.csv');
      })
      .catch((error) => {
        console.error('Export error:', error);
      });
  }, [tabIndex, debouncedSearchQuery, debouncedSearchColumn, vendors.length]);

  const handleOpenPopup = useCallback((vendor) => {
    setVendorToDelete(vendor);
    setOpen(true);
  }, []);

  const handleClosePopup = useCallback(() => {
    setVendorToDelete(null);
    setOpen(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    await dispatch(deleteVendorRequest(vendorToDelete.id));
    handleClosePopup();
  }, [dispatch, vendorToDelete, handleClosePopup]);

  const handleTabChange = useCallback((event, newValue) => {
    setTabIndex(newValue);
    setPage(1);
    // setSearchQuery('');
    // setSearchColumn('');
    // setDebouncedSearchQuery('');
    // setDebouncedSearchColumn('');
  }, []);

  const handlePageChange = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleSort = useCallback(
    (column) => {
      setOrder((prevOrder) => {
        const isAsc = orderBy === column && prevOrder === 'asc';
        return isAsc ? 'desc' : 'asc';
      });
      setOrderBy(column);
    },
    [orderBy]
  );

  const filteredVendors = useMemo(() => {
    const transformedVendors = vendors.map((vendor) => ({
      vendor_serial_no: vendor.vendor_serial_no || '-',
      agency_name: vendor.agency_name || '-',
      full_name: vendor.full_name,
      email: vendor.email || '-',
      phone: vendor.phone || '-',
    }));

    return transformedVendors;
  }, [vendors]);

  const filteredVendorByStatus = useMemo(() => {
    return vendors;
  }, [vendors]);

  const sortedFilteredVendors = useMemo(() => {
    if (!orderBy) return filteredVendorByStatus;

    return [...filteredVendorByStatus].sort((a, b) => {
      let valA, valB;

      if (orderBy === 'fullName') {
        const firstNameA = a.first_name ? a.first_name.trim() : '';
        const lastNameA = a.last_name ? a.last_name.trim() : '';
        valA = `${firstNameA} ${lastNameA}`.trim().toLowerCase();

        const firstNameB = b.first_name ? b.first_name.trim() : '';
        const lastNameB = b.last_name ? b.last_name.trim() : '';
        valB = `${firstNameB} ${lastNameB}`.trim().toLowerCase();
      } else {
        valA = a[orderBy] ? a[orderBy].toString().toLowerCase() : '';
        valB = b[orderBy] ? b[orderBy].toString().toLowerCase() : '';
      }

      if (valA < valB) return order === 'asc' ? -1 : 1;
      if (valA > valB) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredVendorByStatus, orderBy, order]);

  const paginatedData = useMemo(() => {
    return sortedFilteredVendors;
  }, [sortedFilteredVendors]);

  const customColumnOptions = useMemo(() => {
    const keys =
      filteredVendors.length > 0
        ? Object.keys(filteredVendors[0])
        : ['vendor_serial_no', 'agency_name', 'full_name', 'email', 'phone'];

    return keys.map((key) => {
      if (key === 'vendor_serial_no') {
        return { label: 'Vendor ID', value: key };
      } else if (key === 'agency_name') {
        return { label: 'Company Name', value: key };
      } else if (key === 'full_name') {
        return { label: 'Full Name', value: key };
      } else if (key === 'phone') {
        return { label: 'Mobile', value: key };
      } else {
        const label = key
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        return { label, value: key };
      }
    });
  }, [filteredVendors]);

  const totalCount = response?.totalRecords || filteredVendorByStatus.length;
  const totalPages = Math.ceil(totalCount / rowsPerPage);

  if (viewListLoading) {
    return <Loading text='Loading Vendor details...' />;
  }

  return (
    <>
      <ToastContainer />
      <Box className='container'>
        <Grid container spacing={5}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <SearchAndButtonTab
                  title='Vendors'
                  columns={customColumnOptions}
                  hideSearchColumnSelector={false}
                  searchQuery={searchQuery}
                  setSearchQuery={handleSearch}
                  searchColumn={searchColumn}
                  setSearchColumn={handleColumnChange}
                  handleExport={handleExport}
                  permissions={permissions}
                  routeName={ADD_VENDOR}
                  user={user}
                  type='vendor'
                  searchPlaceholder='Search vendors'
                />
                <Tabs
                  value={tabIndex}
                  onChange={handleTabChange}
                  indicatorColor='primary'
                  textColor='primary'
                  sx={{ marginBottom: '30px', gap: 5 }}
                >
                  <Tab label='Pending' />
                  <Tab label='Approved' />
                  <Tab label='Rejected' />
                </Tabs>

                {error ? (
                  <Box textAlign='center' py={5}>
                    <p>
                      Error:{' '}
                      {typeof error === 'string'
                        ? error
                        : error?.message || 'An error occurred'}
                    </p>
                  </Box>
                ) : (
                  <>
                    {paginatedData.length > 0 && (
                      <ReusableTable
                        data={paginatedData}
                        headers={customHeaders(tabIndex,page)}
                        navigate={navigate}
                        paginatedData={paginatedData}
                        handleView={
                          permissions?.vendors?.view || user?.user_type === 1
                            ? (vendor) => {
                                navigate(
                                  `${VIEW_VENDOR.replace(
                                    ':vendorId',
                                    vendor.id
                                  )}`,
                                  {
                                    state: { vendorDetails: vendor,tab:tabIndex,page:page,searchQuery:searchQuery },
                                  }
                                );
                              }
                            : undefined
                        }
                        handleEdit={
                          permissions?.vendors?.edit || user?.user_type === 1
                            ? (vendor) => navigate(`/vendors/edit/${vendor.id}`,{
                                    state: { vendorDetails: vendor,tabEdit:tabIndex,pageEdit:page,searchQuery:searchQuery },
                                  })
                            : undefined
                        }
                        handleOpenPopup={
                          permissions?.vendors?.delete || user?.user_type === 1
                            ? handleOpenPopup
                            : undefined
                        }
                        onSort={handleSort}
                        order={order}
                        orderBy={orderBy}
                      />
                    )}
                    {
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '100%',
                        }}
                      >
                        {paginatedData.length === 0 && (
                          <img
                            src={nodata}
                            alt='nodata'
                            style={{ maxWidth: '300px', maxHeight: '300px' }}
                          />
                        )}
                      </div>
                    }

                    <Box className='button-box'>
                      {totalCount > 0 && (
                        <Pagination
                          count={totalPages}
                          page={page}
                          onChange={handlePageChange}
                          color='primary'
                        />
                      )}
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Dialog open={open} onClose={handleClosePopup}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete{' '}
                <strong>{vendorToDelete?.agency_name}</strong>?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                size='small'
                onClick={handleClosePopup}
                className='handle-back'
              >
                Cancel
              </Button>
              <CustomButton
                variant='contained'
                className='handle-delete'
                type='button'
                size='small'
                loading={deleteLoading}
                text={'DELETE'}
                onClick={() => handleConfirmDelete()}
              />
            </DialogActions>
          </Dialog>
        </Grid>
      </Box>
    </>
  );
};

export default VendorManagementList;
